/**
 * Contrast Utilities
 * Functions to check and fix color contrast issues for accessibility
 */

/**
 * Converts a hex color to RGB
 * @param hex The hex color code
 * @returns RGB color values
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Handle shorthand hex codes
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

/**
 * Converts an RGB color to hex
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Hex color code
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return '#' + [r, g, b]
    .map(x => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');
};

/**
 * Calculates the relative luminance of a color
 * @param r Red component (0-255)
 * @param g Green component (0-255)
 * @param b Blue component (0-255)
 * @returns Relative luminance value (0-1)
 */
export const calculateLuminance = (r: number, g: number, b: number): number => {
  // Convert RGB values to sRGB values
  const sR = r / 255;
  const sG = g / 255;
  const sB = b / 255;
  
  // Convert sRGB values to linear RGB values
  const R = sR <= 0.03928 ? sR / 12.92 : Math.pow((sR + 0.055) / 1.055, 2.4);
  const G = sG <= 0.03928 ? sG / 12.92 : Math.pow((sG + 0.055) / 1.055, 2.4);
  const B = sB <= 0.03928 ? sB / 12.92 : Math.pow((sB + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance using the formula
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

/**
 * Calculates contrast ratio between two colors
 * @param color1 First color (hex)
 * @param color2 Second color (hex)
 * @returns Contrast ratio (1-21)
 */
export const calculateContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  const luminance1 = calculateLuminance(rgb1.r, rgb1.g, rgb1.b);
  const luminance2 = calculateLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  // Determine lighter and darker colors
  const lighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);
  
  // Calculate contrast ratio
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Checks if a contrast ratio meets WCAG standards
 * @param ratio Contrast ratio to check
 * @param level 'AA' or 'AAA' compliance level
 * @param isLargeText Whether the text is considered large
 * @returns Whether the contrast meets the specified level
 */
export const meetsContrastStandard = (
  ratio: number,
  level: 'AA' | 'AAA' = 'AA',
  isLargeText: boolean = false
): boolean => {
  if (level === 'AA') {
    return isLargeText ? ratio >= 3 : ratio >= 4.5;
  } else {
    return isLargeText ? ratio >= 4.5 : ratio >= 7;
  }
};

/**
 * Adjusts a color to meet contrast requirements with another color
 * @param color Color to adjust (hex)
 * @param bgColor Background color to compare against (hex)
 * @param level WCAG compliance level ('AA' or 'AAA')
 * @param isLargeText Whether the text is large
 * @returns Adjusted color that meets contrast requirements
 */
export const adjustColorForContrast = (
  color: string,
  bgColor: string,
  level: 'AA' | 'AAA' = 'AA',
  isLargeText: boolean = false
): string => {
  const targetRatio = level === 'AA' 
    ? (isLargeText ? 3 : 4.5) 
    : (isLargeText ? 4.5 : 7);
  
  const rgb = hexToRgb(color);
  const bgRgb = hexToRgb(bgColor);
  
  // Determine if we should darken or lighten the color
  const colorLuminance = calculateLuminance(rgb.r, rgb.g, rgb.b);
  const bgLuminance = calculateLuminance(bgRgb.r, bgRgb.g, bgRgb.b);
  
  const shouldDarken = colorLuminance > bgLuminance;
  
  // Adjust color until it meets contrast requirements
  let newColor = { ...rgb };
  let currentRatio = calculateContrastRatio(
    rgbToHex(newColor.r, newColor.g, newColor.b),
    bgColor
  );
  
  // Maximum number of iterations to prevent infinite loops
  let iterations = 0;
  const maxIterations = 100;
  
  while (currentRatio < targetRatio && iterations < maxIterations) {
    if (shouldDarken) {
      // Darken the color
      newColor.r = Math.max(0, newColor.r - 5);
      newColor.g = Math.max(0, newColor.g - 5);
      newColor.b = Math.max(0, newColor.b - 5);
    } else {
      // Lighten the color
      newColor.r = Math.min(255, newColor.r + 5);
      newColor.g = Math.min(255, newColor.g + 5);
      newColor.b = Math.min(255, newColor.b + 5);
    }
    
    currentRatio = calculateContrastRatio(
      rgbToHex(newColor.r, newColor.g, newColor.b),
      bgColor
    );
    
    iterations++;
  }
  
  return rgbToHex(newColor.r, newColor.g, newColor.b);
};

/**
 * Common accessible color pairs that meet WCAG AA standards
 */
export const accessibleColorPairs = {
  // Dark text on light backgrounds
  darkOnLight: {
    text: '#212529', // Very dark gray, almost black
    background: '#f8f9fa', // Very light gray
  },
  // Light text on dark backgrounds
  lightOnDark: {
    text: '#ffffff', // White
    background: '#343a40', // Dark gray
  },
  // Primary colors
  primary: {
    light: '#0043a0', // Darker blue that meets contrast on white
    dark: '#74c0fc', // Lighter blue that meets contrast on dark backgrounds
  },
  // Secondary colors
  secondary: {
    light: '#5a6268', // Dark gray that meets contrast on white
    dark: '#ced4da', // Light gray that meets contrast on black
  },
  // Success/green colors
  success: {
    light: '#1a7431', // Dark green that meets contrast on white
    dark: '#a3e4b7', // Light green that meets contrast on black
  },
  // Warning/orange colors
  warning: {
    light: '#ad6e00', // Dark orange that meets contrast on white
    dark: '#ffd16a', // Light orange that meets contrast on black
  },
  // Danger/red colors
  danger: {
    light: '#c82333', // Dark red that meets contrast on white
    dark: '#f8d0d5', // Light red/pink that meets contrast on black
  }
};

export default {
  hexToRgb,
  rgbToHex,
  calculateLuminance,
  calculateContrastRatio,
  meetsContrastStandard,
  adjustColorForContrast,
  accessibleColorPairs
}; 